import { getPennyTransferDate } from '../../api/lendingAPI/CustomerStateAPIHelper';
import { mergeStates } from '../../iwProvider/StoreHelper';
import {
    DRAW_DOWN_COMPLETE_APPLICATION_DATA,
    DRAW_DOWN_CONFIRM_DATA,
    CONFIRM_DRAW_DOWN,
    DRAW_DOWN_PENDING,
    LEAVE_DRAW_DOWN,
    DRAW_DOWN_COMPLETE_APPLICATION_FUNDING_REQUIREMENTS,
    SELECT_DRAW_DOWN_FUNDING_METHOD,
    SELECT_DRAW_DOWN_REPAYMENT_METHOD,
    NEXT_DRAW_DOWN_STEP_LOADING,
    PREVIOUS_DRAW_DOWN_STEP_LOADING,
    SET_DRAWDOWN_DATA
} from './DrawDown.actions';
import {
    deserialiseFundingRequirements,
    getFundingPaymentOptions,
    getRepaymentOptions
} from './DrawDownHelper';
import {
    DRAW_DOWN_SIGN_DOCUMENTS_DATA,
    SIGN_DOCUMENT_AGREE_DONE,
    SIGN_DOCUMENT_AGREE_PENDING,
    SIGN_DOCUMENT_AGREE_ERROR,
    SIGN_DOCUMENT_BOTTOM_VISIBLE,
    SIGN_DOCUMENT_DROP_AGREE,
    SIGN_DOCUMENT_LOAD_DOCUMENT_CONTENT,
    SIGN_DOCUMENT_SHOW_DOCUMENT_CONTENT,
    SIGN_DOCUMENT_SHOW_DOCUMENT_ERROR,
    SIGN_DOCUMENT_LOAD_SIGNED_DOCUMENT_CONTENT,
    SIGN_DOCUMENT_SHOW_SIGNED_DOCUMENT_CONTENT,
    TOGGLE_DOCUMENT_CHECKBOX
} from '../funding/fundingSteps/signDocumentAction/SignDocuments.actions';
import {
    getSignedDocumentURL,
    getSigningDocumentURL
} from '../funding/fundingSteps/signDocumentAction/SignDocumentsHelper';
import {
    GetStateResponse,
    GetFundingRequirementsCreditFacilityResponse,
    GetLoanDocumentsRequiredCreditFacilityResponse,
    GetLoanDocumentsSignedResponse
} from '@iwoca/lapi-client/edge';
import { GetPaymentMethodResponse } from '@iwoca/lapi-client/v1';

const initialState = {
    ready: false,
    previousDrawDownStepLoading: false,
    nextDrawDownStepLoading: false,
    signingDocumentStatus: null,
    signingError: null,
    confirmButtonEnabled: true,
    amount: null,
    offerId: null,
    step: null,
    onfido: null,
    doneTasks: null,
    totalTasks: null,
    progress: null,
    isNextStepDisabled: null
};

export type DrawDownAction =
    | {
          type: typeof SET_DRAWDOWN_DATA;
          amount: number;
          offerId: string;
      }
    | {
          type: typeof DRAW_DOWN_PENDING;
      }
    | { type: typeof LEAVE_DRAW_DOWN }
    | { type: typeof NEXT_DRAW_DOWN_STEP_LOADING }
    | { type: typeof PREVIOUS_DRAW_DOWN_STEP_LOADING }
    | {
          type: typeof DRAW_DOWN_COMPLETE_APPLICATION_DATA;
          customer_state: GetStateResponse;
          funding_requirement: GetFundingRequirementsCreditFacilityResponse;
          loan_documents: GetLoanDocumentsRequiredCreditFacilityResponse;
      }
    | {
          type: typeof DRAW_DOWN_COMPLETE_APPLICATION_FUNDING_REQUIREMENTS;
          customer_state: GetStateResponse;
          funding_requirement: GetFundingRequirementsCreditFacilityResponse;
      }
    | { type: typeof SIGN_DOCUMENT_BOTTOM_VISIBLE }
    | {
          type: typeof TOGGLE_DOCUMENT_CHECKBOX;
          isDocumentConsentAgreed: boolean;
      }
    | { type: typeof SIGN_DOCUMENT_DROP_AGREE }
    | { type: typeof SIGN_DOCUMENT_AGREE_PENDING }
    | { type: typeof SIGN_DOCUMENT_AGREE_DONE }
    | { type: typeof SIGN_DOCUMENT_AGREE_ERROR }
    | {
          type: typeof SIGN_DOCUMENT_LOAD_DOCUMENT_CONTENT;
          signingDocument: {
              title: string;
              document_id: string;
              unsignedURL: string;
          };
      }
    | {
          type: typeof SIGN_DOCUMENT_LOAD_SIGNED_DOCUMENT_CONTENT;
          signedDocument: {
              title: string;
              document_id: string;
              description: string;
              signedURL: string;
          };
      }
    | {
          type: typeof SIGN_DOCUMENT_SHOW_DOCUMENT_CONTENT;
          signingDocumentContent: GetLoanDocumentsRequiredCreditFacilityResponse;
          documentType: GetLoanDocumentsRequiredCreditFacilityResponse['data']['required_loan_documents'][0]['type'];
      }
    | { type: typeof SIGN_DOCUMENT_SHOW_DOCUMENT_ERROR }
    | {
          type: typeof SIGN_DOCUMENT_SHOW_SIGNED_DOCUMENT_CONTENT;
          signedDocumentContent: GetLoanDocumentsSignedResponse;
      }
    | {
          type: typeof DRAW_DOWN_SIGN_DOCUMENTS_DATA;
          customer_state: GetStateResponse;
          funding_requirement: GetFundingRequirementsCreditFacilityResponse;
          loan_documents: GetLoanDocumentsRequiredCreditFacilityResponse;
      }
    | { type: typeof CONFIRM_DRAW_DOWN }
    | {
          type: typeof DRAW_DOWN_CONFIRM_DATA;
          drawDownAmount: number;
          payment_method: GetPaymentMethodResponse;
      }
    | {
          type: typeof SELECT_DRAW_DOWN_FUNDING_METHOD;
          selectedFunding: string;
      }
    | {
          type: typeof SELECT_DRAW_DOWN_REPAYMENT_METHOD;
          selectedRepayment: number;
      };

//TODO: Once the OnfidoVerificationModalHelper is typed, the onfido type can be inferred e.g.:
// type onfido = ReturnType<typeof deserialiseOnfidoResponse>

type TOnfido = {
    uid: string;
    title: string;
    firstName: string;
    lastName: string;
    roles:
        | 'shareholder'
        | 'applicant'
        | 'director'
        | 'guarantor'
        | 'partner'
        | 'other'
        | 'beneficial_owner'
        | 'authorised_person'[];
    phones: {
        uid: string;
        label: string;
        value: string | null;
    }[];
    onfidoStatus: string;
    addPhoneNumberPending: boolean;
};

type TSelectedFunding = {
    type: string;
    value: string;
    cardLastDigits: string;
    label: string;
    bankDetails: {
        sort_code: string;
        account_number: string;
    };
};

type TSelectedRepayment = {
    type: string;
    value: string;
    cardLastDigits: string;
    issuer: string;
    brand: string;
    label: string;
};

type DrawDownState = {
    ready: boolean;
    previousDrawDownStepLoading: boolean;
    nextDrawDownStepLoading: boolean;
    signingDocumentStatus: string | null;
    signingError: boolean | null;
    confirmButtonEnabled: boolean;
    amount: number | null;
    offerId: string | null;
    step: string | null;
    pennyTransferSent?: boolean;
    completeApplicationActions?: {
        type: string;
        status: string;
        disabled: boolean;
    }[];
    onfido: TOnfido | null;
    doneTasks: number | null;
    totalTasks: number | null;
    progress: number | null;
    isNextStepDisabled: boolean | null;
    fundingPaymentOptions?: any;
    repaymentOptions?: any;
    selectedFunding?: TSelectedFunding | null;
    selectedRepayment?: TSelectedRepayment | null;
};

// eslint-disable-next-line import/no-default-export
export default function (
    state: DrawDownState = initialState,
    action: DrawDownAction
): DrawDownState {
    switch (action.type) {
        case DRAW_DOWN_PENDING:
            return mergeStates(state, { ready: false });

        case LEAVE_DRAW_DOWN:
            return mergeStates(initialState);

        case NEXT_DRAW_DOWN_STEP_LOADING:
            return mergeStates(state, {
                nextDrawDownStepLoading: true
            });

        case PREVIOUS_DRAW_DOWN_STEP_LOADING:
            return mergeStates(state, {
                previousDrawDownStepLoading: true
            });

        case SET_DRAWDOWN_DATA:
            return mergeStates(state, {
                amount: action.amount,
                offerId: action.offerId
            });

        case DRAW_DOWN_COMPLETE_APPLICATION_DATA:
            return mergeStates(state, {
                ready: true,
                step: 'COMPLETE_APPLICATION',
                nextDrawDownStepLoading: false,
                previousDrawDownStepLoading: false
            });

        case DRAW_DOWN_COMPLETE_APPLICATION_FUNDING_REQUIREMENTS:
            return {
                ...state,
                pennyTransferSent: getPennyTransferDate(action.customer_state),
                completeApplicationActions: deserialiseFundingRequirements(
                    action.funding_requirement
                )
            };

        case SIGN_DOCUMENT_BOTTOM_VISIBLE:
            //TODO: this has to be probably document specific
            return mergeStates(state, {
                signingContinueEnabled: true
            });

        case TOGGLE_DOCUMENT_CHECKBOX:
            return mergeStates(state, {
                signingCheckBox: action.isDocumentConsentAgreed ? true : false
            });

        case SIGN_DOCUMENT_DROP_AGREE:
            return mergeStates(state, {
                signingReady: false,
                signingContinueEnabled: false,
                signingAgreePending: false,
                signingUnsignedDownloadURL: '',
                signingSignedDownloadURL: '',
                signingTitle: '',
                signingSubTitle: '',
                signingDocumentContent: '',
                signingDocumentId: null
            });

        case SIGN_DOCUMENT_AGREE_PENDING:
            return mergeStates(state, {
                signingReady: true,
                signingContinueEnabled: false,
                signingAgreePending: true
            });

        case SIGN_DOCUMENT_AGREE_DONE:
            return mergeStates(state, {
                signingReady: false,
                signingContinueEnabled: false,
                signingAgreePending: false,
                signingUnsignedDownloadURL: '',
                signingSignedDownloadURL: '',
                signingTitle: '',
                signingSubTitle: '',
                signingDocumentContent: '',
                signingDocumentId: null
            });

        case SIGN_DOCUMENT_AGREE_ERROR:
            return mergeStates(state, {
                signingContinueEnabled: false,
                signingAgreePending: false,
                signingError: true
            });

        case SIGN_DOCUMENT_LOAD_DOCUMENT_CONTENT:
            return mergeStates(state, {
                signingError: null,
                signedDocumentReady: false,
                signingDownloadURL: getSigningDocumentURL(action),
                signingTitle: action.signingDocument.title,
                signingSubTitle: '',
                signingDocumentId: action.signingDocument.document_id
            });

        case SIGN_DOCUMENT_LOAD_SIGNED_DOCUMENT_CONTENT:
            return mergeStates(state, {
                signedReady: false,
                signedContinueEnabled: false,
                signedDownloadURL: getSignedDocumentURL(action),
                signedTitle: action.signedDocument.title,
                signedSubTitle: '',
                signedDescription: action.signedDocument.description,
                signedDocumentId: action.signedDocument.document_id
            });

        case SIGN_DOCUMENT_SHOW_DOCUMENT_CONTENT:
            return mergeStates(state, {
                signingReady: true,
                signingContinueEnabled: false,
                signingDocumentContent: action.signingDocumentContent,
                documentType: action.documentType
            });

        case SIGN_DOCUMENT_SHOW_DOCUMENT_ERROR:
            return mergeStates(state, {
                signingError: true
            });

        case SIGN_DOCUMENT_SHOW_SIGNED_DOCUMENT_CONTENT:
            return mergeStates(state, {
                signedReady: true,
                signedContinueEnabled: false,
                signedDocumentContent: action.signedDocumentContent
            });

        case DRAW_DOWN_SIGN_DOCUMENTS_DATA:
            return mergeStates(state, {
                ready: true,
                step: 'SIGN_DOCUMENTS',
                nextDrawDownStepLoading: false,
                previousDrawDownStepLoading: false,
                fundingRequirements: deserialiseFundingRequirements(
                    action.funding_requirement
                )
            });

        case CONFIRM_DRAW_DOWN:
            return mergeStates(state, {
                confirmButtonEnabled: false
            });

        case DRAW_DOWN_CONFIRM_DATA:
            return mergeStates(
                state,
                {
                    ready: true,
                    step: 'CONFIRM',
                    selectedInputAmount: action.drawDownAmount,
                    nextDrawDownStepLoading: false,
                    previousDrawDownStepLoading: false,
                    confirmButtonEnabled: true
                },
                getFundingPaymentOptions(action.payment_method),
                getRepaymentOptions(action.payment_method)
            );

        case SELECT_DRAW_DOWN_FUNDING_METHOD:
            return mergeStates(state, {
                ...state,
                selectedFunding: action.selectedFunding
            });

        case SELECT_DRAW_DOWN_REPAYMENT_METHOD:
            return mergeStates(state, {
                ...state,
                selectedRepayment: action.selectedRepayment
            });

        default:
            return state;
    }
}
